import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { saasApi } from '../../utils/saasApi';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { CreditCard, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

export default function BillingPage() {
  const [tx, setTx] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [plans, setPlans] = useState([]);
  const [chosenTenant, setChosenTenant] = useState('');
  const [chosenPlan, setChosenPlan] = useState('');
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(false);
  const [polling, setPolling] = useState(false);
  const [params, setParams] = useSearchParams();

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [x, t, p] = await Promise.all([
        saasApi.listTransactions(),
        saasApi.listTenants(),
        saasApi.listPlans(),
      ]);
      setTx(x); setTenants(t); setPlans(p.filter((pp) => pp.is_active));
    } catch (_) {}
    setLoading(false);
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // Poll for status if redirected back from Stripe.
  useEffect(() => {
    const sid = params.get('session_id');
    if (!sid) return;
    setPolling(true);
    let attempts = 0;
    const tick = async () => {
      attempts += 1;
      try {
        const s = await saasApi.checkoutStatus(sid);
        if (s.payment_status === 'paid') {
          toast.success('Payment received — plan activated');
          setPolling(false);
          setParams({});
          refresh();
          return;
        }
        if (s.status === 'expired') {
          toast.error('Checkout expired');
          setPolling(false); setParams({}); return;
        }
      } catch (_) {}
      if (attempts < 8) setTimeout(tick, 2000);
      else { setPolling(false); setParams({}); }
    };
    tick();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  const startCheckout = async () => {
    if (!chosenTenant || !chosenPlan) {
      toast.error('Pick a tenant and a plan'); return;
    }
    setPaying(true);
    try {
      const res = await saasApi.createCheckout({
        tenant_slug: chosenTenant,
        plan_code: chosenPlan,
        origin_url: window.location.origin,
      });
      if (res.free) {
        toast.success('Free plan activated for tenant');
        refresh();
      } else if (res.url) {
        window.location.assign(res.url);
      }
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Failed to start checkout');
    }
    setPaying(false);
  };

  return (
    <div className="p-8 max-w-6xl">
      <h1
        className="text-2xl font-bold text-zinc-900"
        style={{ fontFamily: 'Manrope' }}
        data-testid="sa-billing-title"
      >
        Billing
      </h1>
      <p className="text-sm text-zinc-500 mt-1">
        Sell subscriptions via Stripe checkout and review payment history.
      </p>

      <div className="mt-6 bg-white border rounded-xl p-5">
        <h3 className="font-semibold text-zinc-900 mb-3">Subscribe a tenant</h3>
        <div className="flex flex-wrap items-end gap-4">
          <div className="space-y-1.5 w-64">
            <label className="text-xs font-medium text-zinc-700">Tenant</label>
            <Select value={chosenTenant} onValueChange={setChosenTenant}>
              <SelectTrigger data-testid="sa-billing-tenant-select"><SelectValue placeholder="Choose tenant" /></SelectTrigger>
              <SelectContent>
                {tenants.map((t) => (
                  <SelectItem key={t.slug} value={t.slug}>{t.name} ({t.slug})</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5 w-64">
            <label className="text-xs font-medium text-zinc-700">Plan</label>
            <Select value={chosenPlan} onValueChange={setChosenPlan}>
              <SelectTrigger data-testid="sa-billing-plan-select"><SelectValue placeholder="Choose plan" /></SelectTrigger>
              <SelectContent>
                {plans.map((p) => (
                  <SelectItem key={p.code} value={p.code}>
                    {p.name} — ${p.price_monthly}/mo
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button onClick={startCheckout} disabled={paying || !chosenTenant || !chosenPlan}
            className="bg-amber-600 hover:bg-amber-700" data-testid="sa-billing-checkout-btn">
            {paying ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CreditCard className="h-4 w-4 mr-2" />}
            Checkout
          </Button>
          {polling && (
            <div className="text-sm text-amber-700 flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" /> Verifying payment…
            </div>
          )}
        </div>
      </div>

      <div className="mt-6 bg-white border rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 border-b text-left text-zinc-600">
            <tr>
              <th className="px-4 py-3 font-medium">When</th>
              <th className="px-4 py-3 font-medium">Tenant</th>
              <th className="px-4 py-3 font-medium">Plan</th>
              <th className="px-4 py-3 font-medium">Amount</th>
              <th className="px-4 py-3 font-medium">Status</th>
              <th className="px-4 py-3 font-medium">Session</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan="6" className="px-4 py-8 text-center text-zinc-500">Loading…</td></tr>
            ) : tx.length === 0 ? (
              <tr><td colSpan="6" className="px-4 py-8 text-center text-zinc-500">No transactions yet.</td></tr>
            ) : tx.map((r) => (
              <tr key={r.id} className="border-b last:border-0" data-testid={`sa-billing-row-${r.session_id}`}>
                <td className="px-4 py-3 text-zinc-600">{new Date(r.created_at).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short', timeZone: 'Asia/Kolkata' })}</td>
                <td className="px-4 py-3 text-zinc-900">{r.tenant_slug}</td>
                <td className="px-4 py-3 text-zinc-700">{r.plan_code}</td>
                <td className="px-4 py-3 text-zinc-900 font-medium">
                  ${(r.amount || 0).toFixed(2)} {r.currency?.toUpperCase()}
                </td>
                <td className="px-4 py-3">
                  <span className={`text-xs px-2 py-0.5 rounded-full border ${
                    r.payment_status === 'paid'
                      ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                      : r.payment_status === 'pending'
                        ? 'bg-amber-50 text-amber-700 border-amber-200'
                        : 'bg-zinc-50 text-zinc-700 border-zinc-200'
                  }`}>
                    {r.payment_status}
                  </span>
                </td>
                <td className="px-4 py-3 font-mono text-[11px] text-zinc-500 truncate max-w-[200px]">
                  {r.session_id}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
